import {
    IEvent,
    IStandardPagedResponse,
    IStandardResponse,
    TCompanyId,
    TRawFileId,
    TSubjectId
} from "../../common/types";
import {
    IProtocolDefinition,
    IProtocolStepDefinition,
    TProtocolDefinitionId,
    extractMetadata, TProtocolStepDefinitionMetadata, IProtocolStepDefinitionBase
} from "../types/protocolDefinitions";
import {ICreateProtocolDefinitionDTO} from "../types/dtos/ICreateProtocolDefinitionDTO";
import {AxiosResponse} from "axios";
import {apiClient, createRetryingNotificationHandler, RetryConfig} from "../../common/services/apiClient";
import {IProtocolDefinitionDTO} from "../types/dtos/IProtocolDefinitionDTO";
import {IUpdateProtocolDefinitionDTO} from "../types/dtos/IUpdateProtocolDefinitionDTO";
import {IUpdateProtocolStepDefinitionDTO} from "../types/dtos/IUpdateProtocolStepDefinitionDTO";
import {IProtocolStepDefinitionDTO} from "../types/dtos/IProtocolStepDefinitionDTO";
import {ICreateProtocolStepDefinitionDTO} from "../types/dtos/ICreateProtocolStepDefinitionDTO";
import {
    extractProtocolStepMetadata,
    IProtocol,
    IProtocolStep,
    IProtocolStepBase,
    TProtocolId,
    TProtocolStatus, TProtocolStepId,
} from "../types/protocol";
import {IProtocolDTO} from "../types/dtos/IProtocolDTO";
import {ICreateProtocolRequestDTO} from "../types/dtos/ICreateProtocolRequestDTO";
import {IUpdateProtocolRequestDTO} from "../types/dtos/IUpdateProtocolRequestDTO";
import {IProtocolStepDTO} from "../types/dtos/IProtocolStepDTO";
import dayjs from "dayjs";
import {IUpdateProtocolStepRequestDTO} from "../types/dtos/IUpdateProtocolStepRequestDTO";
import {ICreateProtocolStepRequestDTO} from "../types/dtos/ICreateProtocolStepRequestDTO";
import {IProtocolStepFileDTO} from "../types/dtos/IProtocolStepFileDTO";
import {IProtocolStepFile} from "../types/IProtocolStepFile";
import {fromRawFileDTO} from "../../common/services/rawfilesService";
import {validateStepMetadata} from "../types/stepMetadataValidation";
import {IEventDTO} from "@/common/types/dtos/IEventDTO.ts";
import {IProtocolStepEventDTO} from "@/protocols/types/dtos/IProtocolStepEventDTO.ts";
import {IProtocolStepEvent, TProtocolStepEventId} from "@/protocols/types/IProtocolStepEvent.ts";
import {IProtocolRawFile} from "@/protocols/types/IProtocolRawFile.ts";
import {IProtocolRawFileDTO} from "@/protocols/analysis/types/dtos/IProtocolRawFileDTO.ts";
import {ICreateProtocolStepEventDTO} from "@/protocols/types/dtos/ICreateProtocolStepEventDTO.ts";

export const loadProtocolDefinition = async (companyId: TCompanyId, pdefId: TProtocolDefinitionId, steps: boolean = true): Promise<IProtocolDefinition> => {
    const url = `/companies/${companyId}/protocols/defs/${pdefId}`
    const res: AxiosResponse<IStandardResponse<IProtocolDefinitionDTO>> = await apiClient.get(url,
        {
            params: {steps},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Definitions failed."})
        } as RetryConfig)
    const dto = res.data.data
    return {
        id: dto.id,
        name: dto.name,
        instructions: dto.instructions,
        metadata: dto.metadata || {},
        step_ids: dto.step_ids,
        steps: dto.steps?.map(stepDefinitionDTOToStep) || [],
        createdAt: dto.created_at
    }
}

export const loadProtocolDefinitions = async (companyId: TCompanyId): Promise<Array<IProtocolDefinition>> => {
    const url = `/companies/${companyId}/protocols/defs`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolDefinitionDTO>> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Definitions failed."})
        } as RetryConfig)
    return res.data.data.map((dto) => ({
        id: dto.id,
        name: dto.name,
        instructions: dto.instructions,
        metadata: dto.metadata || {},
        step_ids: dto.step_ids,
        steps: [],
        createdAt: dto.created_at
    }))
}


export const createProtocolDefinition = async (companyId: TCompanyId, dto: ICreateProtocolDefinitionDTO): Promise<IProtocolDefinition> => {
    const url = `/companies/${companyId}/protocols/defs`
    const res: AxiosResponse<IStandardResponse<IProtocolDefinitionDTO>> = await apiClient.post(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const protocolDefinitionDTO = res.data.data

    return {
        id: protocolDefinitionDTO.id,
        name: protocolDefinitionDTO.name,
        instructions: protocolDefinitionDTO.instructions,
        metadata: protocolDefinitionDTO.metadata || {},
        step_ids: protocolDefinitionDTO.step_ids,
        steps: [],
        createdAt: protocolDefinitionDTO.created_at,
    }
};
export const updateProtocolDefinition = async (companyId: TCompanyId, pdef: IProtocolDefinition): Promise<IProtocolDefinition> => {
    const url = `/companies/${companyId}/protocols/defs/${pdef.id}`
    const dto: IUpdateProtocolDefinitionDTO = {
        name: pdef.name,
        instructions: pdef.instructions,
        metadata: pdef.metadata,
        step_ids: pdef.steps.map((step) => step.id)
    }
    const res: AxiosResponse<IStandardResponse<IProtocolDefinitionDTO>> = await apiClient.put(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const protocolDefinitionDTO = res.data.data

    return {
        id: protocolDefinitionDTO.id,
        name: protocolDefinitionDTO.name,
        instructions: protocolDefinitionDTO.instructions,
        metadata: protocolDefinitionDTO.metadata || {},
        step_ids: protocolDefinitionDTO.step_ids,
        steps: pdef.steps,  // step ids are not updated nor returned so preserve them
        createdAt: protocolDefinitionDTO.created_at,
    }
};
export const deleteProtocolDefinition = async (companyId: TCompanyId, protocolDefinitionId: TProtocolDefinitionId): Promise<boolean> => {
    const url = `/companies/${companyId}/protocols/defs/${protocolDefinitionId}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url)
    return res.data.data
}

export const createProtocolStepDefinition = async (companyId: TCompanyId, step: Partial<IProtocolStepDefinition>, index: number): Promise<IProtocolStepDefinition> => {
    const url = `/companies/${companyId}/protocols/defs/${step.protocolId}/steps`

    const dto: ICreateProtocolStepDefinitionDTO = {
        name: step.name!,
        kind: step.kind!,
        index,
        metadata: extractMetadata(step)
    }
    const res: AxiosResponse<IStandardResponse<IProtocolStepDefinitionDTO>> = await apiClient.post(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const protocolDefinitionDTO = res.data.data

    return stepDefinitionDTOToStep(protocolDefinitionDTO);
};

export const loadProtocolDefinitionSteps = async (companyId: TCompanyId, id: TProtocolDefinitionId): Promise<Array<IProtocolStepDefinition>> => {
    const url = `/companies/${companyId}/protocols/defs/${id}/steps`

    const res: AxiosResponse<IStandardPagedResponse<IProtocolStepDefinitionDTO>> = await apiClient.get(url, {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Definition Steps failed."})
        } as RetryConfig
    )
    const steps = res.data.data

    return steps.map(stepDefinitionDTOToStep)
}
export const updateProtocolStepDefinition = async (companyId: TCompanyId, step: IProtocolStepDefinition): Promise<IProtocolStepDefinition> => {
    const metadata: TProtocolStepDefinitionMetadata = extractMetadata(step)
    const url = `/companies/${companyId}/protocols/defs/${step.protocolId}/steps/${step.id}`

    const dto: IUpdateProtocolStepDefinitionDTO = {
        name: step.name,
        instructions: step.instructions,
        kind: step.kind,
        metadata: metadata
    }
    const res: AxiosResponse<IStandardResponse<IProtocolStepDefinitionDTO>> = await apiClient.put(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const protocolDefinitionDTO = res.data.data

    // Use the same conversion function for consistency
    return stepDefinitionDTOToStep(protocolDefinitionDTO);
};
export const deleteProtocolStepDefinition = async (companyId: TCompanyId, step: IProtocolStepDefinition): Promise<boolean> => {
    const url = `/companies/${companyId}/protocols/defs/${step.protocolId}/steps/${step.id}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url)
    return res.data.data
}

export const loadProtocol = async (companyId: TCompanyId, pId: TProtocolId, steps: boolean = true): Promise<IProtocol> => {
    const url = `/companies/${companyId}/protocols/${pId}`
    const res: AxiosResponse<IStandardResponse<IProtocolDTO>> = await apiClient.get(url, {
            params: {steps},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: `Loading Protocol ${pId} failed.`})
        } as RetryConfig
    )
    const protocolDTO = res.data.data
    return {
        id: protocolDTO.id,
        pdefId: protocolDTO.pdef_id,
        subjectId: protocolDTO.subject_id,
        status: protocolDTO.status,
        name: protocolDTO.name,
        instructions: protocolDTO.instructions,
        metadata: protocolDTO.metadata || {},
        steps: protocolDTO.steps?.map(stepDTOToStep) || [],
        createdAt: protocolDTO.created_at,
    }
}
export const loadProtocols = async (companyId: TCompanyId, {subjectId, status, pdefId}: {
    subjectId?: TSubjectId,
    status?: TProtocolStatus,
    pdefId?: TProtocolDefinitionId
}): Promise<Array<IProtocol>> => {
    const url = `/companies/${companyId}/protocols`
    // conditionally build params based on passed parameters
    let params = {
        ...(subjectId && {subject_id: subjectId}),
        ...(status && {status}),
        ...(pdefId && {pdef_id: pdefId})
    }
    const res: AxiosResponse<IStandardPagedResponse<IProtocolDTO>> = await apiClient.get(url, {
            params,
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocols failed."})
        } as RetryConfig
    )
    return res.data.data.map((protocolDTO) =>
        ({
            id: protocolDTO.id,
            pdefId: protocolDTO.pdef_id,
            subjectId: protocolDTO.subject_id,
            status: protocolDTO.status,
            name: protocolDTO.name,
            instructions: protocolDTO.instructions,
            metadata: protocolDTO.metadata || {},
            steps: [],
            createdAt: protocolDTO.created_at,
        })
    )
}
export const createProtocol = async (companyId: TCompanyId, subjectId: TSubjectId, pdefId: TProtocolDefinitionId): Promise<IProtocol> => {
    const url = `/companies/${companyId}/protocols`
    const dto: ICreateProtocolRequestDTO = {subject_id: subjectId, pdef_id: pdefId}
    const res: AxiosResponse<IStandardResponse<IProtocolDTO>> = await apiClient.post(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const protocolDTO = res.data.data

    return {
        id: protocolDTO.id,
        pdefId: protocolDTO.pdef_id,
        subjectId: protocolDTO.subject_id,
        status: protocolDTO.status,
        name: protocolDTO.name,
        instructions: protocolDTO.instructions,
        metadata: protocolDTO.metadata || {},
        steps: [],
        createdAt: protocolDTO.created_at,
    }
}
export const updateProtocol = async (companyId: TCompanyId, protocol: IProtocol, config?: RetryConfig): Promise<IProtocol> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}`
    const dto: IUpdateProtocolRequestDTO = {
        name: protocol.name,
        status: protocol.status,
        metadata: protocol.metadata,
    }
    const res: AxiosResponse<IStandardResponse<IProtocolDTO>> = await apiClient.put(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
        ...(config ?? {}),
    } as RetryConfig)
    const protocolDTO = res.data.data

    return {
        id: protocolDTO.id,
        pdefId: protocolDTO.pdef_id,
        subjectId: protocolDTO.subject_id,
        status: protocolDTO.status,
        name: protocolDTO.name,
        instructions: protocolDTO.instructions,
        metadata: protocolDTO.metadata || {},
        steps: protocol.steps, // preserve steps as they are not updated and not returned from api call
        createdAt: protocolDTO.created_at,
    }
}
export const loadProtocolSteps = async (companyId: TCompanyId, id: TProtocolId): Promise<Array<IProtocolStep>> => {
    const url = `/companies/${companyId}/protocols/${id}/steps`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolStepDTO>> = await apiClient.get(url, {
        retry: true,
        maxRetries: 5,
        backoff: 300,
        onRetry: createRetryingNotificationHandler({message: "Loading Protocol Steps failed."})
    } as RetryConfig)

    return res.data.data.map(stepDTOToStep)
}

export const updateProtocolStep = async (companyId: TCompanyId, step: IProtocolStep, config?: RetryConfig): Promise<IProtocolStep> => {
    const url = `/companies/${companyId}/protocols/${step.protocolId}/steps/${step.id}`
    const dto: IUpdateProtocolStepRequestDTO = {
        name: step.name,
        instructions: step.instructions,
        status: step.status,
        start_time: step.startTime?.toISOString(),
        end_time: step.endTime?.toISOString(),
        metadata: extractProtocolStepMetadata(step)
    }
    const res: AxiosResponse<IStandardResponse<IProtocolStepDTO>> = await apiClient.put(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
        ...(config ?? {}),
    } as RetryConfig)
    const stepDTO = res.data.data

    return stepDTOToStep(stepDTO)
}
export const createProtocolStep = async (companyId: TCompanyId, step: IProtocolStep, index: number): Promise<IProtocolStep> => {
    const url = `/companies/${companyId}/protocols/${step.protocolId}/steps`
    const dto: ICreateProtocolStepRequestDTO = {
        kind: step.kind,
        name: step.name,
        instructions: step.instructions,
        status: step.status,
        start_time: step.startTime?.toISOString(),
        end_time: step.endTime?.toISOString(),
        metadata: extractProtocolStepMetadata(step),
        index: index
    }
    const res: AxiosResponse<IStandardResponse<IProtocolStepDTO>> = await apiClient.post(url, JSON.stringify(dto), {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const stepDTO = res.data.data

    return stepDTOToStep(stepDTO)
}

export const deleteProtocolStep = async (companyId: TCompanyId, step: IProtocolStep): Promise<boolean> => {
    const url = `/companies/${companyId}/protocols/${step.protocolId}/steps/${step.id}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url)
    return res.data.data
}

export const associateProtocolStepFiles = async (companyId: TCompanyId, protocolId: TProtocolId, stepId: TProtocolStepId, rawFileId: TRawFileId): Promise<IProtocolStepFile> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/steps/${stepId}/raw_files/${rawFileId}`;
    const res: AxiosResponse<IStandardResponse<IProtocolStepFileDTO>> = await apiClient.post(url, {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const stepDTO = res.data.data

    return {
        protocolStepId: stepDTO.protocol_step_id,
        rawFileId: stepDTO.raw_file_id
    }
}

export const associateProtocolFiles = async (companyId: TCompanyId, protocolId: TProtocolId, rawFileId: TRawFileId): Promise<IProtocolStepFile> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/raw_files/${rawFileId}`;
    const res: AxiosResponse<IStandardResponse<IProtocolStepFileDTO>> = await apiClient.post(url, {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
    })
    const stepDTO = res.data.data

    return {
        protocolStepId: stepDTO.protocol_step_id,
        rawFileId: stepDTO.raw_file_id
    }
}
export const loadProtocolStepFiles = async (companyId: TCompanyId, protocolId: TProtocolId, stepId: TProtocolStepId): Promise<Array<IProtocolRawFile>> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/steps/${stepId}/raw_files`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolRawFileDTO>> = await apiClient.get(url, {
        headers: {...window.BIOS.headers},
    })
    return res.data.data.map((dto: IProtocolRawFileDTO) => fromProtocolRawFileDTO(dto))
}

export const loadProtocolFiles = async (companyId: TCompanyId, protocolId: TProtocolId, config?: RetryConfig): Promise<Array<IProtocolRawFile>> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/raw_files`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolRawFileDTO>> = await apiClient.get(url, {
        headers: {...window.BIOS.headers},
        ...(config ?? {})
    })
    return res.data.data.map((dto: IProtocolRawFileDTO) => fromProtocolRawFileDTO(dto))
}

export const deleteProtocolStepFile = async (companyId: TCompanyId, pid: TProtocolId, sid: TProtocolStepId, fid: TRawFileId) => {
    const url = `/companies/${companyId}/protocols/${pid}/steps/${sid}/raw_files/${fid}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url)
    return res.data.data
}

export const deleteProtocolFile = async (companyId: TCompanyId, pid: TProtocolId, fid: TRawFileId) => {
    const url = `/companies/${companyId}/protocols/${pid}/raw_files/${fid}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url)
    return res.data.data
}


export const loadProtocolEvents = async (companyId: TCompanyId, pId: TProtocolId): Promise<Array<IProtocolStepEvent>> => {
    const url = `/companies/${companyId}/protocols/${pId}/events`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolStepEventDTO>> = await apiClient.get(url, {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: `Loading Protocol Events failed.`})
        } as RetryConfig
    )
    const pEvents = res.data.data
    return pEvents.map((pEventDTO) => psEventDTOTopsEvent(pEventDTO))
}
export const createProtocolStepEvent = async ({
                                                  companyId,
                                                  protocolId,
                                                  stepId,
                                                  dto,
                                                  config,
                                              }: {
                                                  companyId: TCompanyId,
                                                  protocolId: TProtocolId,
                                                  stepId?: TProtocolStepId,
                                                  dto: ICreateProtocolStepEventDTO,
                                                  config?: RetryConfig
                                              }
): Promise<IProtocolStepEvent> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/events`
    const res: AxiosResponse<IStandardResponse<IProtocolStepEventDTO>> = await apiClient.post(url, JSON.stringify(dto), {
        params: stepId ? {step_id: stepId} : {},
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
        ...(config ?? {}),
    } as RetryConfig)
    const eventDTO = res.data.data
    return psEventDTOTopsEvent(eventDTO)
}
export const deleteProtocolStepEvent = async ({
                                                  companyId,
                                                  protocolId,
                                                  id
                                              }: {
                                                  companyId: TCompanyId,
                                                  protocolId: TProtocolId,
                                                  id: TProtocolStepEventId
                                              },config?: {silent?: boolean}
): Promise<boolean> => {
    const url = `/companies/${companyId}/protocols/${protocolId}/events/${id}`
    const res: AxiosResponse<IStandardResponse<boolean>> = await apiClient.delete(url, {
        headers: {...window.BIOS.headers, "Content-Type": "application/json"},
        ...(config ?? {}),
    })
    return res.data.data
}

const psEventDTOTopsEvent = (eventDTO: IProtocolStepEventDTO): IProtocolStepEvent => {
    return {
        id: eventDTO.id,
        protocolId: eventDTO.protocol_id,
        protocolStepId: eventDTO.protocol_step_id,
        event: eventDTOToEvent(eventDTO.event)
    } as IProtocolStepEvent
}
const eventDTOToEvent = (eventDTO: IEventDTO): IEvent => {
    return {
        id: eventDTO.id,
        eventType: eventDTO.event_type,
        deviceId: eventDTO.device_id,
        subjectId: eventDTO.subject_id,
        tags: eventDTO.tags,
        metadata: eventDTO.metadata,
        startTime: eventDTO.start_time,
        endTime: eventDTO.end_time,
        createdAt: eventDTO.created_at
    } as IEvent
}

const stepDTOToStep = (stepDTO: IProtocolStepDTO): IProtocolStep => {

    const stepBase: IProtocolStepBase = {
        id: stepDTO.id,
        protocolId: stepDTO.protocol_id,
        kind: stepDTO.kind,
        name: stepDTO.name,
        instructions: stepDTO.instructions,
        createdAt: stepDTO.created_at,
        status: stepDTO.status,
        startTime: stepDTO.start_time ? dayjs(stepDTO.start_time) : undefined,
        endTime: stepDTO.end_time ? dayjs(stepDTO.end_time) : undefined,
        ...(stepDTO.files && stepDTO.files.length > 0 ? {
            transient: {
                fileData: {
                    files: stepDTO.files?.map((file) => fromProtocolRawFileDTO(file))
                }
            }
        } : {})
    }

    // Validate metadata fields before spreading
    const validatedMetadata = stepDTO.metadata
        ? validateStepMetadata(stepDTO.kind, stepDTO.metadata as Record<string, unknown>, "stepDTOToStep")
        : {};

    return {...stepBase, ...validatedMetadata} as IProtocolStep
}

const stepDefinitionDTOToStep = (stepDefDTO: IProtocolStepDefinitionDTO): IProtocolStepDefinition => {
    const stepBase: IProtocolStepDefinitionBase = {
        id: stepDefDTO.id,
        kind: stepDefDTO.kind,
        protocolId: stepDefDTO.protocol_def_id,
        name: stepDefDTO.name,
        instructions: stepDefDTO.instructions,
        createdAt: stepDefDTO.created_at,
    }

    // Validate metadata fields before spreading
    const validatedMetadata = stepDefDTO.metadata
        ? validateStepMetadata(stepDefDTO.kind, stepDefDTO.metadata as Record<string, unknown>, "stepDefinitionDTOToStep")
        : {};

    return {...stepBase, ...validatedMetadata} as IProtocolStepDefinition
}

export const fromProtocolRawFileDTO = (rfdto : IProtocolRawFileDTO) : IProtocolRawFile => {
    const rf = fromRawFileDTO(rfdto)
    return {
        ...rf,
        protocolId: rfdto.protocol_id,
        protocolStepId: rfdto.protocol_step_id
    } as IProtocolRawFile
}